import { useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Box,
  CloseButton,
  createListCollection,
  Field,
  Flex,
  Input,
  InputGroup,
  Portal,
  Select,
  Textarea,
} from '@valwin/facto/components';
import { FileUpIcon } from '@valwin/facto/icons';

import type { FacebookPage } from '~features/integrations/facebook/types';
import { useErrorTranslator } from '~shared/i18n';
import { ImageUploadCrop } from '~shared/ui';

import { useFacebookShareForm } from '../context';
import { type FacebookShareFormErrors, MAX_MESSAGE_LENGTH } from '../types';

type FacebookShareFormProps = {
  pages: FacebookPage[];
  shareUrl: string;
  onImageUpload: (file: File) => Promise<string>;
};

export function FacebookShareForm({
  pages,
  shareUrl,
  onImageUpload,
}: FacebookShareFormProps) {
  const { t } = useTranslation();

  const translateError = useErrorTranslator<FacebookShareFormErrors>({
    PAGE_REQUIRED: () => t('errors fieldRequired'),
    PHOTO_REQUIRED: () => t('errors imageRequired'),
  });

  const {
    register,
    control,
    trigger,
    formState: { errors },
  } = useFacebookShareForm();

  const message = useWatch({ control, name: 'message' });
  const messageLength = message?.length ?? 0;

  const pagesCollection = useMemo(
    () =>
      createListCollection({
        items: pages.map((p) => ({ label: p.pageName, value: p.pageId })),
      }),
    [pages]
  );

  return (
    <Flex direction="column" gap="4">
      <Field.Root required invalid={!!errors.pageId}>
        <Field.Label>
          {t('integrations facebook share publish page')}{' '}
          <Field.RequiredIndicator />
        </Field.Label>

        {pages.length > 1 ? (
          <Controller
            control={control}
            name="pageId"
            render={({ field }) => (
              <Select.Root
                name={field.name}
                value={[field.value]}
                onValueChange={({ value }) => field.onChange(value[0])}
                onInteractOutside={() => field.onBlur()}
                collection={pagesCollection}
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText
                      placeholder={t('integrations facebook share select page')}
                    />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal>
                  <Select.Positioner>
                    <Select.Content>
                      {pagesCollection.items.map((page) => (
                        <Select.Item item={page} key={page.value}>
                          {page.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>
            )}
          />
        ) : (
          <Input readOnly disabled value={pages[0].pageName} />
        )}
        <Field.ErrorText>
          {errors.pageId?.message && translateError(errors.pageId.message)}
        </Field.ErrorText>
      </Field.Root>

      <Field.Root required invalid={!!errors.message}>
        <Field.Label>
          {t('thread message')} <Field.RequiredIndicator />
        </Field.Label>
        <Textarea
          placeholder={t('integrations facebook share message')}
          {...register('message', { onChange: () => trigger('message') })}
          rows={6}
          minHeight="75px"
          maxHeight="150px"
        />
        <Field.HelperText alignSelf="end">
          <Box as="span" color={errors.message?.message ? 'red' : 'inherit'}>
            {messageLength}
          </Box>{' '}
          / {MAX_MESSAGE_LENGTH}
        </Field.HelperText>
      </Field.Root>

      <Field.Root required invalid={!!errors.photos}>
        <Field.Label>
          {t('richTextEditor image tooltip')} <Field.RequiredIndicator />
        </Field.Label>
        <Controller
          name="photos"
          control={control}
          render={({ field }) => (
            <ImageUploadCrop.Root
              currentImage={field.value[0]}
              ratio={{ x: 1080, y: 1350 }}
              onCrop={async (file) => {
                const url = await onImageUpload(file);

                field.onChange([url]);
              }}
              onClear={() => field.onChange([])}
            >
              <InputGroup
                startElement={<FileUpIcon size="sm" />}
                endElement={
                  <ImageUploadCrop.ClearTrigger>
                    <CloseButton
                      me="-1"
                      size="xs"
                      colorPalette="gray"
                      focusVisibleRing="inside"
                      focusRingWidth="2px"
                      pointerEvents="auto"
                    />
                  </ImageUploadCrop.ClearTrigger>
                }
              >
                <Input asChild cursor="pointer">
                  <ImageUploadCrop.Trigger>
                    <ImageUploadCrop.FileText
                      placeholder={t(
                        'integrations facebook share select image'
                      )}
                    />
                  </ImageUploadCrop.Trigger>
                </Input>
              </InputGroup>
              <ImageUploadCrop.Dialog />
            </ImageUploadCrop.Root>
          )}
        />
        <Field.ErrorText>
          {errors.photos?.message && translateError(errors.photos.message)}
        </Field.ErrorText>
      </Field.Root>

      <Field.Root>
        <Field.Label>{t('integrations facebook share url shared')}</Field.Label>
        <Input readOnly disabled value={shareUrl} />
      </Field.Root>
    </Flex>
  );
}
